'use client';

import { useEffect } from 'react';

/** Custom events used as GTM triggers — do not rename (container depends on them). */
export const GTM_READY_EVENT = 'hzc_gtm_ready';
export const MARKETING_READY_EVENT = 'hzc_marketing_ready';

/**
 * gtm.js is loaded by the standard snippet in layout <head>, so GA4 / Ads tags on
 * "All Pages" fire at parse time and short sessions are still counted.
 *
 * This component only emits the two custom events the container may already use as
 * triggers: `hzc_gtm_ready` right away, `hzc_marketing_ready` after idle so heavy
 * pixels (Firebase, Meta, Revotas, efilli) stay off the critical path.
 */
export default function GTMReadySignals({ gtmId }: { gtmId: string }) {
  useEffect(() => {
    if (!gtmId) return;

    window.dataLayer = window.dataLayer || [];

    if (!window.hzc_gtm_ready) {
      window.hzc_gtm_ready = true;
      window.dataLayer.push({ event: GTM_READY_EVENT });
    }

    if (window.hzc_marketing_ready) return;

    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const fire = () => {
      if (window.hzc_marketing_ready) return;
      window.hzc_marketing_ready = true;
      window.dataLayer.push({ event: MARKETING_READY_EVENT });
    };

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(() => fire(), { timeout: 3000 });
    }
    // Hard cap so marketing tags fire even when idle never comes (busy or hidden tab).
    timeoutId = setTimeout(fire, 2000);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (idleId !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [gtmId]);

  return null;
}
